import React from "react";
import { IonPage, IonContent, IonAlert } from "@ionic/react";
import { useTranslation } from "react-i18next";

interface Props {
  title: string;
  subtitle?: string;
  icon?: string;
  alert?: { open: boolean; header: string; message: string; onClose: () => void; dismissLabel: string };
  onBack?: () => void;
  backLabel?: string;
}

/**
 * Gabarit commun aux écrans d'authentification.
 * Mobile : formulaire plein écran. Grand écran : panneau de marque à gauche, formulaire à droite.
 */
const AuthShell: React.FC<Props> = ({ title, subtitle, icon = "fa-graduation-cap", alert, onBack, backLabel, children }) => {
  const { t } = useTranslation();
  const iconClass = icon.startsWith("fa-brands") ? icon : `fa-solid ${icon}`;
  return (
    <IonPage>
      <IonContent>
        {alert && <IonAlert isOpen={alert.open} onDidDismiss={alert.onClose} header={alert.header} message={alert.message} buttons={[alert.dismissLabel]} />}
        <div className="auth-layout">
          <div className="auth-brand">
            <div className="auth-brand-inner">
              <div className="sidebar-logo auth-brand-logo">
                <i className="fa-solid fa-graduation-cap" />
              </div>
              <h2 className="text-4xl font-black text-white mt-6 mb-2">Scholeo</h2>
              <p className="text-lg text-white/90 mb-8">{t("brand.tagline")}</p>
              <ul className="space-y-3 text-white/85">
                {["p1", "p2", "p3"].map((k) => (
                  <li key={k} className="flex gap-3 items-start">
                    <i className="fa-solid fa-circle-check mt-1 text-success-300" />
                    <span>{t(`brand.points.${k}`)}</span>
                  </li>
                ))}
              </ul>
              <p className="text-xs text-white/60 mt-10">{t("brand.per")}</p>
            </div>
          </div>

          <div className="auth-form">
            {onBack && (
              <button type="button" onClick={onBack} className="auth-back">
                <i className="fa-solid fa-arrow-left" />
                <span className="text-sm font-semibold">{backLabel}</span>
              </button>
            )}
            <div className="w-full max-w-sm animate-fade-in">
              <div className="flex justify-center mb-6 auth-form-icon">
                <div className="w-24 h-24 bg-gradient-primary rounded-3xl flex items-center justify-center shadow-2xl">
                  <i className={`${iconClass} text-5xl text-white`} />
                </div>
              </div>
              <div className="text-center mb-7">
                <h1 className="text-3xl font-black text-white mb-2 tracking-tight">{title}</h1>
                {subtitle && <p className="text-gray-400">{subtitle}</p>}
              </div>
              {children}
            </div>
          </div>
        </div>
      </IonContent>
    </IonPage>
  );
};

export default AuthShell;

export const inputClass =
  "w-full px-4 py-3.5 bg-white/10 border-2 border-white/20 rounded-xl text-white placeholder-gray-500 text-base focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-all";

export const labelClass = "text-xs font-bold text-gray-300 ml-1 uppercase tracking-wider flex items-center gap-2";
